/**
 * Where the browser keeps content for offline practice.
 *
 * IndexedDB rather than `localStorage`: a course runs to tens of megabytes and
 * `localStorage` caps out around five, synchronously, on the main thread. The Cache API
 * would also work, but this stores parsed payloads rather than responses, so what comes
 * back has already been validated against the contract once.
 *
 * Every method swallows its failure. A browser in private mode, with storage disabled,
 * or over quota should cost the learner offline content — not the lesson they are in.
 */
import type { ResponseStore } from '@vibelang/api-client';

const DB_NAME = 'vibelang.offline';
const STORE = 'responses';
const VERSION = 1;

interface Entry {
  readonly key: string;
  readonly value: unknown;
  readonly bytes: number;
  readonly storedAt: number;
}

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const request = globalThis.indexedDB?.open(DB_NAME, VERSION);
      if (request === undefined) return resolve(null);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Runs one transaction and resolves with its result, or `null` if anything goes wrong. */
async function withStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T | null> {
  const db = await open();
  if (db === null) return null;
  return new Promise((resolve) => {
    try {
      const request = run(db.transaction(STORE, mode).objectStore(STORE));
      request.onsuccess = () => resolve(request.result as T);
      request.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export class IndexedDbStore implements ResponseStore {
  async read(key: string): Promise<unknown | null> {
    const entry = await withStore<Entry | undefined>('readonly', (store) => store.get(key));
    return entry?.value ?? null;
  }

  async write(key: string, value: unknown, bytes: number): Promise<void> {
    const entry: Entry = { key, value, bytes, storedAt: Date.now() };
    await withStore('readwrite', (store) => store.put(entry));
  }

  async size(): Promise<number> {
    const entries = await withStore<Entry[]>('readonly', (store) => store.getAll());
    return (entries ?? []).reduce((sum, entry) => sum + entry.bytes, 0);
  }

  async clear(): Promise<void> {
    await withStore('readwrite', (store) => store.clear());
  }

  /** What is held, so the settings screen can say how much and since when. */
  async describe(): Promise<{ count: number; bytes: number }> {
    const entries = (await withStore<Entry[]>('readonly', (store) => store.getAll())) ?? [];
    return {
      count: entries.length,
      bytes: entries.reduce((sum, entry) => sum + entry.bytes, 0),
    };
  }
}
